import { useEffect, useRef, useState } from "react";

interface Stat {
	value: number;
	suffix: string;
	label: string;
	decimals?: number;
}

const stats: Stat[] = [
	{ value: 5000, suffix: "+", label: "Healthcare Professionals" },
	{ value: 50000, suffix: "+", label: "Happy Patients" },
	{ value: 200000, suffix: "+", label: "Appointments" },
	{ value: 4.9, suffix: "", label: "Average Rating", decimals: 1 },
];

function formatNumber(value: number, decimals?: number): string {
	if (decimals !== undefined) return value.toFixed(decimals);
	if (value >= 1000) {
		return value.toLocaleString("en-US");
	}
	return value.toString();
}

function useCountUp(target: number, duration: number = 2000, decimals?: number, started: boolean = false) {
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (!started) return;
		let startTime: number | null = null;
		const step = (timestamp: number) => {
			if (!startTime) startTime = timestamp;
			const progress = Math.min((timestamp - startTime) / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 3); // ease out cubic
			setCurrent(parseFloat((eased * target).toFixed(decimals ?? 0)));
			if (progress < 1) requestAnimationFrame(step);
		};
		requestAnimationFrame(step);
	}, [target, duration, decimals, started]);

	return current;
}

function StatItem({ stat, started }: { stat: Stat; started: boolean }) {
	const count = useCountUp(stat.value, 2000, stat.decimals, started);

	return (
		<div className="flex flex-col items-center gap-2 flex-1 py-2">
			<span
				className="font-bold text-white"
				style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", lineHeight: 1.1, letterSpacing: "-0.01em" }}
			>
				{formatNumber(count, stat.decimals)}{stat.suffix}
			</span>
			<span
				className="text-white text-center"
				style={{ fontSize: "clamp(0.8rem, 1.5vw, 0.95rem)", opacity: 0.85, fontWeight: 400 }}
			>
				{stat.label}
			</span>
		</div>
	);
}

export default function StatsBar() {
	const ref = useRef<HTMLDivElement>(null);
	const [started, setStarted] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setStarted(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.3 }
		);
		if (ref.current) observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return (
		<section
			ref={ref}
			className="w-full"
			style={{ background: "#F5A623" }}
		>
			<div className="max-w-6xl mx-auto px-6 py-12">
				<div className="flex flex-col sm:flex-row items-center justify-between gap-8 sm:gap-4 divide-y sm:divide-y-0 sm:divide-x divide-white/20">
					{stats.map((stat) => (
						<StatItem key={stat.label} stat={stat} started={started} />
					))}
				</div>
			</div>
		</section>
	);
}